import axios from 'axios';
import { getTokens } from '../authService';
import { BACKEND_URL } from '../../config/env';

/**
 * Uploads an image to the express server using formData and axios so that the upload progress can be tracked.
 * @param {string} galleryID - The ID of the gallery to add the image to.
 * @param {File} image - The image to upload to the server
 * @param {(val: number) => void} setProgress - The callback which to set the upload progress
 */
export const uploadImage = async (galleryID, image, setProgress = (_v) => {}) => {
	console.log('File: ', image);
	const signedUploadUrl = await generateSignedUploadUrl(
		galleryID,
		image.name,
		image.type,
		setProgress
	);
	await uploadImageToSignedUrl(signedUploadUrl, image, image.type, setProgress);
	const imageUrl = signedUploadUrl.split('?')[0];
	await addImageToGallery(imageUrl, galleryID, setProgress);

	const optimized = imageUrl?.replace('original', 'optimized')?.replace(/\.[^/.]+$/, '.webp');
	return optimized;
};

/**
 * Generates a signed URL for uploading an image to a gallery.
 * @param {string} galleryID - The ID of the gallery to add the image to
 * @param {string} filename - The name of the image file
 * @param {string} contentType - The MIME type of the image
 * @param {function} setProgress - Function to track upload progress
 * @returns {Promise<string>} - The signed URL for uploading the image
 */
const generateSignedUploadUrl = async (galleryID, filename, contentType, setProgress) => {
	const { idToken } = getTokens();
	const weight = 0.05;

	const url = `${BACKEND_URL}/app/images/gallery-image-upload-url`;
	const body = { galleryID, filename, contentType };

	try {
		const response = await axios.post(url, body, {
			headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
			onUploadProgress: (e) => setUploadProgress(e, setProgress, weight)
		});
		console.log('response.data: ', response.data);
		return response.data.url;
	} catch (error) {
		console.error('Error generating signed url:', error);
		throw error;
	}
};

/**
 * Uploads an image using a signed URL.
 * @param {string} signedUrl - The signed URL to upload the image
 * @param {File} file - The file to be uploaded
 * @param {string} contentType - The MIME type of the file
 * @param {function} [setProgress] - Optional function to track upload progress
 * @returns {Promise<void>}
 */
const uploadImageToSignedUrl = async (signedUrl, file, contentType, setProgress) => {
	const weight = 0.85;
	const offset = 5;
	const config = {
		headers: { 'Content-Type': contentType },
		onUploadProgress: (e) => setUploadProgress(e, setProgress, weight, offset)
	};

	const response = await axios.put(signedUrl, file, config);
	console.log('response.data: ', response.data);
};

/**
 * Adds an image to the gallery by associating the uploaded image URL with the gallery.
 * @param {string} url - The bucket URL where the image is stored
 * @param {string} galleryID - The ID of the gallery to add the image to
 * @param {function} [setProgress] - Optional function to track upload progress
 * @returns {Promise<void>}
 */
const addImageToGallery = async (url, galleryID, setProgress) => {
	const { idToken } = getTokens();
	const weight = 0.1;
	const offset = 90;

	const endpoint = `${BACKEND_URL}/app/images/gallery-image`;
	const body = { galleryID, url };

	const config = {
		headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
		onUploadProgress: (e) => setUploadProgress(e, setProgress, weight, offset)
	};

	const response = await axios.post(endpoint, body, config);

	console.log('response.data: ', response.data);
};

/**
 * Sets the upload progress based on the event object.
 * @param {ProgressEvent} event - The progress event object from the upload
 * @param {function} setProgress - Function to update the progress
 * @param {number} weight - The weight of this step in the overall progress
 * @param {number} [offset=0] - The starting point for this progress step
 */
const setUploadProgress = (event, setProgress, weight, offset = 0) => {
	const progress = offset + Math.round(weight * ((event.loaded * 100) / event.total));
	console.log('Progress: ', progress);
	setProgress(progress);
};

/**
 * Gets gallery stats for the given gallery ID
 */
export const getGalleryImages = async (galleryID) => {
	const { idToken } = getTokens();

	const response = await fetch(`${BACKEND_URL}/app/images/gallery-images/${galleryID}`, {
		method: 'GET',
		headers: { 'Content-type': 'application/json', Authorization: `Bearer ${idToken}` }
	});

	const galleryStatsResponse = await response?.json();

	return galleryStatsResponse;
};
